import { useReducer, useCallback, useEffect, useMemo, useState } from 'react';
import { defaultTo } from 'lodash/fp';
import { debounce } from 'app/lib/fp';
import { useEventCallback } from 'app/lib/hooks/useEventCallback/useEventCallback.js';

const DEFAULT_DELAY_MS = 25;

const defaultToEmptyString = defaultTo('');

const initInputValueReducer = (initialValue) => ({
    value: defaultToEmptyString(initialValue),
    lastValue: null
});

const inputValueReducer = (state, { type, value, initialValue }) => {
    switch (type) {
        case 'CHANGE':
            return { lastValue: state.value, value: defaultToEmptyString(value) };
        case 'RESET':
            return { lastValue: state.value, value: defaultToEmptyString(initialValue) };
        case 'UNSET':
            return { lastValue: null, value: '' };
        default:
            throw new Error(`useInputValue uncaught action type in reducer: ${type}`);
    }
};

export const useInputValue = (initialValue, delay = DEFAULT_DELAY_MS) => {
    const [inputValue, setInputValue] = useState(defaultToEmptyString(initialValue));

    const [{ value, lastValue }, dispatch] = useReducer(
        inputValueReducer,
        initialValue,
        initInputValueReducer
    );

    const debouncedDispatch = useMemo(
        () => debounce({ leading: true, trailing: true, maxWait: delay }, delay, dispatch),
        [delay]
    );

    const set = useCallback(
        (value) => {
            setInputValue(defaultToEmptyString(value));
            debouncedDispatch({ type: 'CHANGE', value });
        },
        [debouncedDispatch]
    );

    const setByEvent = useEventCallback(set);

    const reset = useCallback(() => {
        setInputValue(defaultToEmptyString(initialValue));
        debouncedDispatch({ type: 'RESET', initialValue });
    }, [initialValue, debouncedDispatch]);

    const unset = useCallback(() => {
        setInputValue('');
        debouncedDispatch({ type: 'UNSET' });
    }, [debouncedDispatch]);

    useEffect(() => () => debouncedDispatch.cancel(), [debouncedDispatch]);

    return {
        immediateValue: inputValue,
        debouncedValue: value,
        previousDebouncedValue: lastValue,
        dispatch,
        set,
        setByEvent,
        reset,
        unset
    };
};
